/**
 * Browser-side .xlsx generation/parsing via `exceljs` (already a workspace
 * dependency — `packages/import/package.json` pins the same 4.4.0 — reused
 * here rather than adding a second library for the same job). `exceljs`
 * ships a proper `browser` build (`dist/exceljs.min.js`), so this runs in
 * Vite without a Node-core-module shim.
 *
 * The real backend has no file-upload or file-serving endpoint for import
 * (see `dataImportExport.ts`'s doc comment) — the sample "file" a user
 * downloads and the file they upload are both handled entirely client-side:
 * the sample is generated from the real `GET .../crm/leads/import/template`
 * JSON, and an uploaded workbook is parsed back into the same row shape the
 * real `POST .../crm/leads/import` endpoint accepts.
 *
 * `exceljs` is loaded via a dynamic `import()` in every function below
 * rather than a static top-level import: it nearly doubles this app's main
 * bundle (measured — 355KB → 629KB gzip), and every other route in this app
 * never touches it, so Vite code-splits it into its own chunk that only
 * loads when a viewer actually opens this one settings page.
 */
import type { LeadImportRow, LeadImportTemplate } from '../types/dataImportExport.js';

const SHEET_NAME = 'سرنخ‌ها';

export async function buildSampleWorkbookBlob(template: LeadImportTemplate): Promise<Blob> {
  const { default: ExcelJS } = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(SHEET_NAME, { views: [{ rightToLeft: true }] });
  sheet.addRow([...template.headers]);
  sheet.addRow([...template.sampleRow]);
  sheet.getRow(1).font = { bold: true };
  sheet.columns.forEach((column) => {
    column.width = 24;
  });
  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export interface ParsedRow {
  readonly index: number;
  readonly row: LeadImportRow;
  /** Client-side check only — the real per-row errors come back from the API's own response. */
  readonly missingTitle: boolean;
}

export interface ParsedWorkbook {
  readonly rows: readonly ParsedRow[];
  /** Header cells in the sheet that don't match a known column — surfaced, not silently dropped. */
  readonly unknownHeaders: readonly string[];
}

const KNOWN_HEADERS = new Set<string>(['title', 'sourceLabel', 'contactPhone', 'contactEmail']);

/** Reads the first worksheet of an uploaded `.xlsx`, matching columns by their header name (row 1) — not by position, so column order in the filled file doesn't matter. */
export async function parseLeadWorkbook(file: File): Promise<ParsedWorkbook> {
  const { default: ExcelJS } = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  const buffer = await file.arrayBuffer();
  await workbook.xlsx.load(buffer);
  const sheet = workbook.worksheets[0];
  if (!sheet) return { rows: [], unknownHeaders: [] };

  const headerRow = sheet.getRow(1);
  const columnIndexByHeader = new Map<string, number>();
  const unknownHeaders: string[] = [];
  headerRow.eachCell((cell, colNumber) => {
    const header = String(cell.value ?? '').trim();
    if (!header) return;
    if (KNOWN_HEADERS.has(header)) {
      columnIndexByHeader.set(header, colNumber);
    } else {
      unknownHeaders.push(header);
    }
  });

  const rows: ParsedRow[] = [];
  let rowIndex = 0;
  sheet.eachRow((sheetRow, rowNumber) => {
    if (rowNumber === 1) return;
    const cellText = (header: string): string | undefined => {
      const colNumber = columnIndexByHeader.get(header);
      if (!colNumber) return undefined;
      const value = sheetRow.getCell(colNumber).value;
      if (value === null || value === undefined) return undefined;
      const text = String(value).trim();
      return text.length > 0 ? text : undefined;
    };
    const title = cellText('title');
    const sourceLabel = cellText('sourceLabel');
    const contactPhone = cellText('contactPhone');
    const contactEmail = cellText('contactEmail');
    // An entirely blank row (e.g. trailing spreadsheet padding) is not a row.
    if (!title && !sourceLabel && !contactPhone && !contactEmail) return;
    rows.push({
      index: rowIndex,
      row: {
        title: title ?? '',
        ...(sourceLabel !== undefined ? { sourceLabel } : {}),
        ...(contactPhone !== undefined ? { contactPhone } : {}),
        ...(contactEmail !== undefined ? { contactEmail } : {}),
      },
      missingTitle: !title,
    });
    rowIndex += 1;
  });

  return { rows, unknownHeaders };
}

export async function buildErrorReportBlob(
  errors: readonly { readonly index: number; readonly message: string }[],
): Promise<Blob> {
  const { default: ExcelJS } = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('خطاها', { views: [{ rightToLeft: true }] });
  sheet.addRow(['ردیف', 'پیام خطا']);
  sheet.getRow(1).font = { bold: true };
  for (const error of errors) {
    // +2: header row plus 1-based row numbering, matching what the user sees in the spreadsheet.
    sheet.addRow([error.index + 2, error.message]);
  }
  sheet.columns.forEach((column) => {
    column.width = 28;
  });
  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}
